// Working copy
/* eslint-disable react/no-danger */
import React, { useEffect, useState, useContext, useMemo } from 'react';
import { graphql } from 'gatsby';
import classNames from 'classnames';
import SEO from '../components/SEO';
import VersionDefault from '../components/Templates/VersionDefault';
import VersionLegacy from '../components/Templates/VersionLegacy';
import VersionA from '../components/Templates/VersionA';
import ShopifyLayout from '../layout/ShopifyLayout';
import { StoreContext } from '../context/store-context';
import { AuthContext } from '../context/auth-context';
import { getConvertedCategoryFields } from '../data/ShopifyCollectionCategoryData';

const ns = `page-category`;

const Category = ({
  location,
  data,
  pageContext: { handle },
}) => {
  const memoizedData = useMemo(() => {
    const {
      category,
      wp: {
        acfOptionsCategory: {
          categorySettings: {
            productsVisible,
            promoCard,
            campaignCopy,
          },
        },
      },
    } = data;

    return { category, productsVisible, promoCard, campaignCopy };
  }, [data]);

	const { category, productsVisible, promoCard, campaignCopy } = memoizedData;

	const rootClassnames = classNames({
		[`${ ns }`]: true,
		[`${ ns }-${ handle }`]: true,
	});
	const { state: authState } = useContext(AuthContext);
	const { userPropertiesLoaded } = authState;
	const [elevarUserEventLoaded, setElevarUserEventLoaded] = useState(false);
	const { elevarUserDataEvent, cartLoading } = useContext(StoreContext);

	useEffect(() => {
		if (userPropertiesLoaded && !cartLoading) {
			elevarUserDataEvent(location);
			setElevarUserEventLoaded(true);
		}
	}, [userPropertiesLoaded, cartLoading]);

	const convertedTypedFields = getConvertedCategoryFields(category);
	const categoryWithTypedFields = { ...convertedTypedFields	};

	const renderTemplate = () => {
		switch (category.collectionTemplate?.value) {
			case 'VersionLegacy':
				return (
					<VersionLegacy
						category={categoryWithTypedFields}
						location={location}
						productsVisible={productsVisible}
						promoCard={promoCard}
						campaignCopy={campaignCopy}
					/>
				);
			case 'VersionA':
				return (
					<VersionA
						productsVisible={productsVisible}
						category={categoryWithTypedFields}
						location={location}
					/>
				);
			default:
				return (
					<VersionDefault
						category={categoryWithTypedFields}
						location={location}
						productsVisible={productsVisible}
						promoCard={promoCard}
						campaignCopy={campaignCopy}
					/>
				);
		}
	};

	return (
		<ShopifyLayout location={location}>
			<div className={rootClassnames} style={{background: 'white'}}>
				<SEO
					title={category?.seo?.title || category.title}
					image={category?.image?.originalSrc}
					description={category?.seo?.description || category?.description}
					page={handle}
					link={location.href}
				/>
				{renderTemplate()}
			</div>
		</ShopifyLayout>
	);
};

export default Category;

export const query = graphql`
	query($handle: String!) {
		category: shopifyCollection(handle: {eq: $handle}) {
			title
			handle
			description
			descriptionHtml
			metafields {
				key
				value
				namespace
			}
			image {
				altText
				originalSrc
			}
			seo {
				title
				description
			}
			products {
				shopifyId
				id
				title
				handle
				tags
				createdAt
				storefrontId
				slug: gatsbyPath(
					filePath: "/products/{ShopifyProduct.productType}/{ShopifyProduct.handle}"
				)
				images: media {
					... on ShopifyMediaImage {
						image {
							altText
							src
							gatsbyImageData(aspectRatio: 1, width: 640)
						}
					}
				}
				priceRangeV2 {
					maxVariantPrice {
						amount
						currencyCode
					}
					minVariantPrice {
						amount
						currencyCode
					}
				}
				vendor
				collections {
					title
				}
				variants {
					availableForSale
					inventoryQuantity
					shopifyId
					storefrontId
					sku
					title
					price
					compareAtPrice
					selectedOptions {
						name
						value
					}
					image {
						altText
						originalSrc
					}
					metafields {
						key
						value
						namespace
					}
				}
				options {
					name
					values
					shopifyId
				}
				metafields {
					key
					value
					namespace
				}
			}
			#########################
			# Collection METAFIELDS #
			#########################
			heroFontIsDark: metafield(
				namespace: "headless",
				key: "hero_font_is_dark"
			) {
				value
			}
			heroBullets: metafield(
				key: "hero_bullet",
				namespace: "custom"
			) {
				value
			}
			activeCampaign: metafield(
				key: "active_campaign",
				namespace: "custom"
			) {
				value
			}
			copyOne: metafield(
				key: "copy_one",
				namespace: "custom"
			) {
				value
			}
			productsOne: metafield(
				key: "products_one",
				namespace: "custom"
			) {
				value
			}
			productListingTitle: metafield(
				key: "product_listing_title",
				namespace: "custom"
			) {
				value
			}
			productListingDescription: metafield(
				key: "product_listing_description",
				namespace: "custom"
			) {
				value
			}
			copyTwo: metafield(
				key: "copy_two",
				namespace: "custom"
			) {
				value
			}
			videoContentSectionHeadline: metafield(
				key: "video_content_section_headline",
				namespace: "custom"
			) {
				value
			}
			videoContentSectionUrl: metafield(
				key: "video_content_section_url",
				namespace: "custom"
			) {
				value
			}
			productsTwo: metafield(
				key: "products_two",
				namespace: "custom"
			) {
				value
			}
			copyThree: metafield(
				key: "copy_three",
				namespace: "custom"
			) {
				value
			}
			productsThree: metafield(
				key: "products_three",
				namespace: "custom"
			) {
				value
			}
			copyFour: metafield(
				key: "copy_four",
				namespace: "custom"
			) {
				value
			}
			productsFour: metafield(
				key: "products_four",
				namespace: "custom"
			) {
				value
			}
			copyFive: metafield(
				key: "copy_five",
				namespace: "custom"
			) {
				value
			}
			productsFive: metafield(
				key: "products_five",
				namespace: "custom"
			) {
				value
			}
			copySix: metafield(
				key: "copy_six",
				namespace: "custom"
			) {
				value
			}
			productsSix: metafield(
				key: "products_six",
				namespace: "custom"
			) {
				value
			}
			copySeven: metafield(
				key: "copy_seven",
				namespace: "custom"
			) {
				value
			}
			productsSeven: metafield(
				key: "products_seven",
				namespace: "custom"
			) {
				value
			}
			copyEight: metafield(
				key: "copy_eight",
				namespace: "custom"
			) {
				value
			}
			productsEight: metafield(
				key: "products_eight",
				namespace: "custom"
			) {
				value
			}
			contentSection1Image: metafield(
				key: "content_section_1_image",
				namespace: "custom"
			) {
				value
			}
			contentSection1Copy: metafield(
				key: "content_section_1_copy",
				namespace: "custom"
			) {
				value
			}
			contentSection2Image: metafield(
				key: "content_section_2_image",
				namespace: "custom"
			) {
				value
			}
			contentSection2Copy: metafield(
				key: "content_section_2_copy",
				namespace: "custom"
			) {
				value
			}
			contentSection4Image: metafield(
				key: "content_section_4_image",
				namespace: "custom"
			) {
				value
			}
			contentSection4Copy: metafield(
				key: "content_section_4_copy",
				namespace: "custom"
			) {
				value
			}
			faqs: metafield(
				key: "faqs",
				namespace: "custom"
			) {
				value
			}
			collectionIconSvg: metafield(
				key: "collection_icon_svg",
				namespace: "custom"
			) {
				value
			}
			promo: metafield(
				key: "promo",
				namespace: "custom"
			) {
				value
			}
			promoText: metafield(
				key: "promo_text",
				namespace: "custom"
			) {
				value
			}
			promoAmount: metafield(
				key: "promo_amount",
				namespace: "custom"
			) {
				value
			}
			fullWidthBannerTitle: metafield(
				key: "full_width_banner_title",
				namespace: "custom"
			) {
				value
			}
			fullWidthBannerDescription: metafield(
				key: "full_width_banner_description",
				namespace: "custom"
			) {
				value
			}
			fullWidthBannerLinkLabel: metafield(
				key: "full_width_banner_link_label",
				namespace: "custom"
			) {
				value
			}
			fullWidthBannerLinkUrl: metafield(
				key: "full_width_banner_link_url",
				namespace: "custom"
			) {
				value
			}
			fullWidthBannerImageAltText: metafield(
				key: "full_width_banner_image_alt_text",
				namespace: "custom"
			) {
				value
			}
			fullWidthBannerImageSourceUrl: metafield(
				key: "full_width_banner_image_source_url",
				namespace: "custom"
			) {
				value
			}
			fullWidthBannerFontIsDark: metafield(
        key: "full_width_banner_font_is_dark",
        namespace: "custom"
      ) {
        value
      }
			bannerImageSourceUrlDesktop: metafield(
				key: "banner_image_source_url_desktop",
				namespace: "headless"
			) {
				value
			}
			bannerImageSourceUrlTablet: metafield(
				key: "banner_image_source_url_tablet",
				namespace: "headless"
			) {
				value
			}
			bannerImageSourceUrlMobile: metafield(
				key: "banner_image_source_url_mobile",
				namespace: "headless"
			) {
				value
			}
			heroImageSourceUrlDesktop: metafield(
				key: "hero_image_source_url_desktop",
				namespace: "headless"
			) {
				value
			}
			heroImageSourceUrlTablet: metafield(
				key: "hero_image_source_url_tablet",
				namespace: "headless"
			) {
				value
			}
			heroImageSourceUrlMobile: metafield(
				key: "hero_image_source_url_mobile",
				namespace: "headless"
			) {
				value
			}
			heroEnableWave: metafield(
				key: "enable_wave",
				namespace: "custom"
			) {
				value
			}
			collectionTemplate: metafield(
				key: "collection_template",
				namespace: "custom"
			) {
				value
			}
			heroTitle: metafield(
				key: "hero_title",
				namespace: "headless"
			) {
				value
			}
			heroDescription: metafield(
				key: "hero_description",
				namespace: "headless"
			) {
				value
			}
			plpHeroImages: metafield(
				key: "plp_hero_images",
				namespace: "custom"
			) {
				value
			}
			heroHeadline: metafield(
				key: "hero_headline",
				namespace: "custom"
			) {
				value
			}
		}
		wp {
			acfOptionsCategory {
				categorySettings {
					productsVisible {
						initialAmount
						nextAmount
					}
					promoCard {
						active
						title
						subtitle
						link {
							label
							url
						}
					}
					campaignCopy
				}
			}
		}
	}
`;
